import React, { useState, useEffect } from 'react';
import { portfolioService } from '../../lib/portfolioService';
import {
  Plus,
  Search,
  Eye,
  Edit,
  Users,
  BarChart,
  Calendar,
  Target,
  FileText,
  Activity,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  X,
  Filter,
  Download
} from 'lucide-react';

interface ClientCard {
  id: string;
  company_name: string;
  email: string;
  status: string;
  tier: string;
  logo_url?: string;
  created_at: string;
  metadata?: {
    industry?: string;
    company_size?: string;
  };
  performance?: {
    total_posts?: number;
    total_engagements?: number;
    engagement_rate?: number;
    platforms_count?: number;
    plugins_count?: number;
  };
}

export default function ClientPortfoliosEnhanced() {
  const [clients, setClients] = useState<ClientCard[]>([]);
  const [filteredClients, setFilteredClients] = useState<ClientCard[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState<ClientCard | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    filterClients();
  }, [clients, searchTerm, statusFilter, tierFilter]);

  const loadClients = async () => {
    setLoading(true);
    setError(null);

    const result = await portfolioService.getPortfolios();

    if (result.success) {
      setClients(result.data);
    } else {
      setError('Failed to load client portfolios');
    }

    setLoading(false);
  };

  const filterClients = () => {
    let filtered = [...clients];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(c =>
        c.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => c.status === statusFilter);
    }

    // Tier filter
    if (tierFilter !== 'all') {
      filtered = filtered.filter(c => c.tier === tierFilter);
    }

    setFilteredClients(filtered);
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      onboarding: 'bg-blue-100 text-blue-800',
      churned: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getTierBadge = (tier: string) => {
    const badges: Record<string, { color: string; icon: string }> = {
      basic: { color: 'bg-gray-100 text-gray-800', icon: '🥉' },
      standard: { color: 'bg-blue-100 text-blue-800', icon: '🥈' },
      premium: { color: 'bg-purple-100 text-purple-800', icon: '🥇' },
      enterprise: { color: 'bg-yellow-100 text-yellow-800', icon: '👑' }
    };
    const badge = badges[tier] || badges.standard;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.color} flex items-center gap-1`}>
        <span>{badge.icon}</span>
        {tier}
      </span>
    );
  };

  const formatNumber = (num?: number) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const handleViewClient = async (client: ClientCard) => {
    setSelectedClient(client);
    setShowDetailsModal(true);
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Client Portfolios</h1>
            <p className="text-gray-600">Manage and monitor all client accounts</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            New Client
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-800">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search clients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="onboarding">Onboarding</option>
                <option value="inactive">Inactive</option>
                <option value="churned">Churned</option>
              </select>
            </div>

            {/* Tier Filter */}
            <div>
              <select
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Tiers</option>
                <option value="basic">Basic</option>
                <option value="standard">Standard</option>
                <option value="premium">Premium</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
          </div>

          {/* Stats Summary */}
          <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{clients.length}</div>
              <div className="text-sm text-gray-600">Total Clients</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {clients.filter(c => c.status === 'active').length}
              </div>
              <div className="text-sm text-gray-600">Active</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {clients.filter(c => c.status === 'onboarding').length}
              </div>
              <div className="text-sm text-gray-600">Onboarding</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {clients.filter(c => c.tier === 'premium' || c.tier === 'enterprise').length}
              </div>
              <div className="text-sm text-gray-600">Premium+</div>
            </div>
          </div>
        </div>
      </div>

      {/* Client Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading portfolios...</p>
        </div>
      ) : filteredClients.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Users className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600">No clients found matching your filters</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredClients.map(client => (
            <div
              key={client.id}
              className="bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => handleViewClient(client)}
            >
              {/* Header */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {client.logo_url ? (
                      <img src={client.logo_url} alt={client.company_name} className="w-12 h-12 rounded-lg object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                        {client.company_name.charAt(0)}
                      </div>
                    )}
                    <div>
                      <h3 className="font-semibold text-gray-900">{client.company_name}</h3>
                      <p className="text-xs text-gray-600">{client.email}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(client.status)}`}>
                    {client.status}
                  </span>
                  {getTierBadge(client.tier)}
                </div>
              </div>

              {/* Metadata */}
              {client.metadata && (
                <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Industry:</span>
                    <span className="font-medium text-gray-900">{client.metadata.industry || 'N/A'}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs mt-1">
                    <span className="text-gray-600">Company Size:</span>
                    <span className="font-medium text-gray-900">{client.metadata.company_size || 'N/A'}</span>
                  </div>
                </div>
              )}

              {/* Performance Metrics */}
              {client.performance && (
                <div className="p-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Total Posts</div>
                      <div className="text-lg font-bold text-gray-900">
                        {formatNumber(client.performance.total_posts)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Engagements</div>
                      <div className="text-lg font-bold text-gray-900">
                        {formatNumber(client.performance.total_engagements)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Engagement Rate</div>
                      <div className="text-lg font-bold text-green-600">
                        {client.performance.engagement_rate || 0}%
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Platforms</div>
                      <div className="text-lg font-bold text-blue-600">
                        {client.performance.platforms_count || 0} / {client.performance.plugins_count || 0}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                <span className="text-xs text-gray-600">
                  Joined {new Date(client.created_at).toLocaleDateString()}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewClient(client);
                    }}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Handle edit
                    }}
                    className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {selectedClient.logo_url ? (
                  <img src={selectedClient.logo_url} alt={selectedClient.company_name} className="w-12 h-12 rounded-lg" />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xl">
                    {selectedClient.company_name.charAt(0)}
                  </div>
                )}
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{selectedClient.company_name}</h2>
                  <p className="text-sm text-gray-600">{selectedClient.email}</p>
                </div>
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Quick Stats */}
                <div className="col-span-1 md:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <FileText className="w-8 h-8 text-blue-600 mb-2" />
                    <div className="text-2xl font-bold text-gray-900">
                      {formatNumber(selectedClient.performance?.total_posts)}
                    </div>
                    <div className="text-sm text-gray-600">Total Posts</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <TrendingUp className="w-8 h-8 text-green-600 mb-2" />
                    <div className="text-2xl font-bold text-gray-900">
                      {formatNumber(selectedClient.performance?.total_engagements)}
                    </div>
                    <div className="text-sm text-gray-600">Engagements</div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <BarChart className="w-8 h-8 text-purple-600 mb-2" />
                    <div className="text-2xl font-bold text-gray-900">
                      {selectedClient.performance?.engagement_rate || 0}%
                    </div>
                    <div className="text-sm text-gray-600">Engagement Rate</div>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <Users className="w-8 h-8 text-yellow-600 mb-2" />
                    <div className="text-2xl font-bold text-gray-900">
                      {selectedClient.performance?.platforms_count || 0}
                    </div>
                    <div className="text-sm text-gray-600">Platforms</div>
                  </div>
                </div>

                {/* Tabs would go here for Notes, Goals, Team, etc. */}
                <div className="col-span-1 md:col-span-3 text-center text-gray-500 py-12">
                  More details coming soon...
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
